import { Check, LayoutGrid, Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { specFor, type Board, type SavedLayout } from "@shared/dashboardLayout";
import type { GridWidget } from "./DashboardGrid";

export function BoardControls({ board, layout, widgets, editing, onStart, onDone, onReset, onAdd }: {
  board: Board;
  layout: SavedLayout;
  widgets: Record<string, GridWidget>;
  editing: boolean;
  onStart: () => void;
  onDone: () => void;
  onReset: () => void;
  onAdd: (id: string) => void;
}) {
  if (!editing) {
    return (
      <Button variant="outline" size="sm" className="h-7 hidden lg:inline-flex" onClick={onStart}>
        <LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Customize
      </Button>
    );
  }
  const onGrid = new Set(layout.items.map((i) => i.id));
  const ids = Object.keys(widgets);
  const defaults = ids.filter((id) => !specFor(board, id)?.extra);
  const extras = ids.filter((id) => specFor(board, id)?.extra);
  const row = (id: string) => {
    const w = widgets[id];
    const present = onGrid.has(id);
    return (
      <div key={id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{w.title}</p>
          <p className="text-xs text-muted-foreground truncate">{w.description}</p>
        </div>
        <Button size="sm" variant={present ? "ghost" : "default"} className="h-7 shrink-0" disabled={present} onClick={() => onAdd(id)}>
          {present ? "On dashboard" : "Add"}
        </Button>
      </div>
    );
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-500/10 px-2 py-1">
        Drag to move, drag an edge to resize, × removes
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7"><Plus className="w-3.5 h-3.5 mr-1" /> Add widget</Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0 max-h-[70vh] overflow-auto">
          <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Default widgets</p>
          {defaults.map(row)}
          {extras.length > 0 && (
            <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">More widgets</p>
          )}
          {extras.map(row)}
        </PopoverContent>
      </Popover>
      <Button variant="outline" size="sm" className="h-7" onClick={onReset}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default</Button>
      <Button size="sm" className="h-7" onClick={onDone}><Check className="w-3.5 h-3.5 mr-1" /> Done</Button>
    </div>
  );
}
